import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Inject,
  forwardRef,
  ViewChild,
  AfterViewInit,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges,
  ViewChildren,
  QueryList
} from '@angular/core';
import {
  FormActivity,
  FormLayoutModes,
  FormLayoutModel,
  TwoColumnFormLayoutModel,
  DataEntityLayoutModel,
  SingleColumnFormLayoutModel,
  DataEntityRowLayoutModel,
  DataEntityGroupLayoutModel,
  DataEntityTableLayoutModel
} from '../../../../models/activities';
import { DataEntity, ParentReference } from '../../../../models/data-entities';
import {
  WorkflowService,
  WorkflowContextService,
  Utilities
} from '../../../../services';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivityEditorBaseComponent } from '../activity-editor-base/activity-editor-base.component';
import autoScroll from 'dom-autoscroller';
import { Workflow } from '../../../../models';
import { DragulaService } from 'ng2-dragula';
import { DataEntityEditorComponent } from '../../data-entities/data-entity-editor/data-entity-editor.component';
import { ToastrService } from 'ngx-toastr';
import { DataEntityItemComponent } from './data-entity-item/data-entity-item.component';
import { DataEntityItemToolButton } from './data-entity-item/data-entity-item.component';
import {
  CdkDragDrop,
  moveItemInArray,
  transferArrayItem
} from '@angular/cdk/drag-drop';
import {
  LAYOUT_DROP_LISTS,
  registerLayoutDropList,
  unregisterLayoutDropList
} from '../../data-entities/layout-drop-lists';

@Component({
  selector: 'wm-form-activity-editor',
  templateUrl: './form-activity-editor.component.html',
  styleUrls: ['./form-activity-editor.component.css'],
  standalone: false
})
export class FormActivityEditorComponent extends ActivityEditorBaseComponent
  implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  @Input() activity: FormActivity;
  localActivity: FormActivity;
  showNewEditor = false;
  newDEForm: UntypedFormGroup;
  newDE: DataEntity;
  @ViewChild('newDEEditor', { static: false })
  newEditor: DataEntityEditorComponent;
  @ViewChildren('entityItem')
  entityList: QueryList<DataEntityItemComponent>;

  @Input() workflow: Workflow;
  @Input() includedDataEntityTypes?: string[];
  @Input() excludedDataEntityTypes?: string[];
  @Input() id: string = '';
  @Input() showLayoutDropdown: boolean = true;
  /**
   * Optional caller-supplied buttons to render inside every column entity's
   * tool panel. Threaded straight through to `wm-data-entity-item`.
   */
  @Input() extraToolButtons: DataEntityItemToolButton[] = [];

  isFiltered: boolean;
  formLayouts: { display: string; value: number }[];
  selected: string[];
  dragulaKey = 'form-Entities';

  get saving() {
    return this._workflowSvc.saving;
  }
  showEditor = false;

  ngAfterViewInit(): void {
    this._dragulaService.dropModel().subscribe(value => {
      this.onDropDataEntity(value);
    });

    this._ref.detectChanges();

    if (this.loadTemplateCode) {
      const lee = this.entityList
        .toArray()
        .find(f => f.de?.entity?.templateCode == this.loadTemplateCode);

      if (lee) {
        lee.openEditor();
        this._ref.detectChanges();
      } else {
        this.toastr.warning('Entity could not be found');
      }
    }
  }

  addRowLayout(event?: Event) {
    event?.preventDefault();
    // Implementation for row layout
    this.localActivity.model.formLayoutModel.addLayout(
      new DataEntityRowLayoutModel()
    );

    this.saved.emit(this.localActivity);
  }

  addGroupLayout(event?: Event) {
    event?.preventDefault();
    // Implementation for group layout
    this.localActivity.model.formLayoutModel.addLayout(
      new DataEntityGroupLayoutModel()
    );
    this.saved.emit(this.localActivity);
  }

  addTableLayout(event?: Event) {
    event?.preventDefault();
    // Implementation for table layout
    this.localActivity.model.formLayoutModel.addLayout(
      new DataEntityTableLayoutModel()
    );
    this.saved.emit(this.localActivity);
  }

  initFormLayouts() {
    const keys = Object.keys(FormLayoutModes);

    this.formLayouts = keys.slice(keys.length / 2).map((index, layout) => {
      return {
        value: layout,
        display: Utilities.addSpaceToTitleCase(FormLayoutModes[layout])
      };
    });
  }

  changeLayout(e) {
    this.localActivity.model.formLayoutModel = FormLayoutModel.getModeModel(
      this.localActivity,
      parseFloat(this.localActivity.model.formLayout.toString()),
      this.localActivity.model.formLayoutModel
    );
  }

  constructor(
    @Inject(forwardRef(() => WorkflowService))
    public _workflowSvc: WorkflowService,
    @Inject(forwardRef(() => WorkflowContextService))
    public _context: WorkflowContextService,
    private toastr: ToastrService,
    protected _fb: UntypedFormBuilder,
    private _dragulaService: DragulaService,
    public _ref: ChangeDetectorRef
  ) {
    super();
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activity']) {
      this.localActivity = this.activity;
    }
  }

  entitiesToExcluded() {
    if (this.localActivity.model.formLayoutModel) {
      return (<TwoColumnFormLayoutModel>(
        this.localActivity.model.formLayoutModel
      )).leftColumnEntities.concat(
        (<TwoColumnFormLayoutModel>this.localActivity.model.formLayoutModel)
          .rightColumnEntities
      );
    }
  }

  removeDataEntity(de, parentList) {
    this.localActivity.model.formLayoutModel.removeEntity(de);
    this.saved.emit(this.localActivity);
  }

  get layoutEntities() {
    if (this.isFiltered) {
      return null;
    } else {
      return (this.localActivity.model
        .formLayoutModel as SingleColumnFormLayoutModel)?.columnEntities;
    }
  }

  set layoutEntities(value: any[]) {
    if (!this.isFiltered) {
      (this.localActivity.model
        .formLayoutModel as SingleColumnFormLayoutModel).columnEntities = value;
    }
  }

  filterEntities(
    entities: DataEntityLayoutModel[],
    includeAllLayouts: boolean = false
  ) {
    this.isFiltered = false;
    let filteredEntities = entities;

    if (!includeAllLayouts) {
      filteredEntities = entities.filter(de => de.entity);
    }

    if ((this.includedDataEntityTypes || []).length > 0) {
      filteredEntities = entities.filter(
        de =>
          this.includedDataEntityTypes.indexOf(de.entity.dataEntityTypeCode) >
          -1
      );
      this.isFiltered = entities.length !== filteredEntities.length;
    } else if ((this.excludedDataEntityTypes || []).length > 0) {
      filteredEntities = entities.filter(
        de =>
          this.excludedDataEntityTypes.indexOf(de.entity.dataEntityTypeCode) ===
          -1
      );
      this.isFiltered = entities.length !== filteredEntities.length;
    }

    return filteredEntities;
  }
  sortEntities(entities: DataEntityLayoutModel[]) {
    return entities.sort((a, b) => a.displayOrder - b.displayOrder);
  }

  viewDataEntity(de: DataEntityLayoutModel) { }

  resetNew() {
    this.showEditor = false;
    this.newDEForm = this._fb.group({});
  }

  saveNew(e: any) {
    this.localActivity.addEntity(this.newEditor.dataEntity);

    this.saved.emit(this.localActivity);
    this.resetNew();
  }

  cancelNew() {
    this.showEditor = false;
  }

  loadEditor() {
    this.showEditor = true;
    this.newDEForm = this._fb.group({});
  }

  onSaved(e: any) {
    this.saved.emit(e);
  }

  saveDataEntity(e: any) {
    this.saved.emit(this.localActivity);
  }

  // The main list is a CDK drop list (like the Row / Group lists) so that data
  // entities can be dragged between it and any Row or Group.
  readonly mainDropListId = registerLayoutDropList('main-entities-list');
  readonly connectedDropLists = LAYOUT_DROP_LISTS;

  dropMain(event: CdkDragDrop<DataEntityLayoutModel[]>) {
    if (event.previousContainer === event.container) {
      moveItemInArray(
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    } else {
      // entity coming out of a Row / Group
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
      event.previousContainer.data.forEach((entity, index) => {
        if (entity) {
          entity.displayOrder = index + 1;
        }
      });
    }

    event.container.data.forEach((entity, index) => {
      if (entity) {
        entity.displayOrder = index + 1;
      }
    });

    this.saved.emit(this.localActivity);
  }

  onDropDataEntity(args: any) {
    for (let i = 0; i < args.sourceModel.length; i++) {
      if (args.sourceModel[i]) {
        args.sourceModel[i].displayOrder = i + 1;
      }
    }
  }

  ngOnInit() {
    this.localActivity = this.activity;

    if (!this.workflow) {
      this.workflow = this._context.workflow;
    }
    this.initFormLayouts();

    this.dragulaKey = 'form-Entities' + this.id;

    // Configure dragula to only allow dragging from the handle and prevent dragging when inside a CDK drag-drop area
    // only create the group if it doesn't already exist to prevent errors when multiple editors are open
    if (!this._dragulaService.find(this.dragulaKey)) {
      this._dragulaService.createGroup(this.dragulaKey, {
        moves: (el, container, handle) => {
          // Prevent dragging if we're inside a CDK drag-drop area
          if (handle?.closest('.cdk-drag-container')) {
            return false;
          }
          // Only allow dragging from the drag handle
          return (
            handle?.classList.contains('drag-handle') ||
            handle?.parentElement?.classList.contains('drag-handle')
          );
        }
      });
    }

    // set the parent reference to the activity in the event that the parent reference is not set because it is used down below in the validation logic
    this.activity.model.getEntities().forEach(de => {
      if (!de) {
        return;
      }
      de.parent = new ParentReference({ id: this.activity.id });
    });

    this.newDEForm = this._fb.group({});

    if (!this.localActivity.model.formLayoutModel) {
      if (this.localActivity.model.formLayout === 0) {
        this.localActivity.model.formLayoutModel = new SingleColumnFormLayoutModel(
          this.localActivity
        );
      } else {
        this.localActivity.model.formLayoutModel = new TwoColumnFormLayoutModel(
          this.localActivity
        );
      }
    }

    this.changeLayout(null);

    setTimeout(() => {
      const scroll = autoScroll([window], {
        margin: 20,
        maxSpeed: 5,
        scrollWhenOutside: true,
        autoScroll: function () {
          // Only scroll when the pointer is down.
          return this.down;
        }
      });
    }, 3000);
  }

  ngOnDestroy() {
    unregisterLayoutDropList(this.mainDropListId);
    // Clean up dragula group to prevent memory leaks
    this._dragulaService.destroy(this.dragulaKey);
  }
}
